import { useCallback, useEffect, useRef, useState } from 'react';
import type { Balance, Bet, PrivateEventEnvelope, PublicEventEnvelope, RoundSnapshot, UtcInstant } from '@urbanrats/contracts';
import type { ClientGateways, ConnectionState } from './gateway/index.ts';

export interface RoundOutcome {
  gameRoundId: string;
  kind: 'RESULT' | 'VOIDED';
  finalCount: number | null;
  winning: string[];
}

export interface GameView {
  round: RoundSnapshot | null;
  /** Outcome of the round currently shown (null until result/void). */
  outcome: RoundOutcome | null;
  recent: RoundOutcome[];
  bets: Bet[];
  balance: Balance | null;
  /** The casino wallet gave no balance: betting is locked until it answers. */
  walletDown: boolean;
  connection: ConnectionState;
  sessionExpired: boolean;
  /** Increments on every counted crossing; drives the 250 ms highlight. */
  countPulse: number;
  resync: () => void;
}

const RECENT_MAX = 12;

/**
 * Subscribes to the public round topic and the private player topic.
 * A sequence gap or a reconnect triggers a fresh snapshot instead of guessing.
 */
export function useGame(gw: ClientGateways): GameView {
  const [round, setRound] = useState<RoundSnapshot | null>(null);
  const [outcomes, setOutcomes] = useState<RoundOutcome[]>([]);
  const [bets, setBets] = useState<Bet[]>([]);
  const [balance, setBalance] = useState<Balance | null>(null);
  // The casino wallet did not give a balance: betting stays locked until it answers (retried every 15 s).
  const [walletDown, setWalletDown] = useState(false);
  const [connection, setConnection] = useState<ConnectionState>(gw.connection.get());
  const [sessionExpired, setSessionExpired] = useState(false);
  const [countPulse, setCountPulse] = useState(0);
  const lastSeq = useRef<number | null>(null);
  const lastCount = useRef<{ id: string; count: number } | null>(null);

  const pushOutcome = useCallback((o: RoundOutcome) => {
    setOutcomes((xs) => [o, ...xs.filter((x) => x.gameRoundId !== o.gameRoundId)].slice(0, RECENT_MAX));
  }, []);

  const resync = useCallback(() => {
    void gw.game.getCurrentRound().then((r) => {
      setRound(r);
      lastSeq.current = r?.sequence ?? null;
    });
    // Recent outcomes come from the server's round records, so they survive a reload.
    void gw.game.listRecentRounds(RECENT_MAX + 1).then((rs) =>
      setOutcomes(
        rs.flatMap((r): RoundOutcome[] =>
          r.state === 'VOIDED'
            ? [{ gameRoundId: r.gameRoundId, kind: 'VOIDED', finalCount: null, winning: [] }]
            : r.winningSelections && r.finalCount !== null
              ? [{ gameRoundId: r.gameRoundId, kind: 'RESULT', finalCount: r.finalCount, winning: r.winningSelections }]
              : [],
        ),
      ),
    );
    void gw.game.listBets().then(setBets);
    void gw.wallet.getBalance().then(
      (b) => {
        setBalance(b);
        setWalletDown(false);
      },
      () => setWalletDown(true),
    );
  }, [gw]);

  useEffect(() => {
    resync();
    const onPublic = (e: PublicEventEnvelope) => {
      const p = e.payload;
      if (p.type === 'round.snapshot') {
        lastSeq.current = e.sequence;
        setRound(p.round);
        return;
      }
      if (lastSeq.current !== null && e.sequence !== lastSeq.current + 1) {
        resync();
        return;
      }
      lastSeq.current = e.sequence;
      setRound((r) => {
        if (!r || r.gameRoundId !== e.gameRoundId) return r;
        switch (p.type) {
          case 'round.state':
            return { ...r, state: p.to, sequence: e.sequence };
          case 'round.crossing':
            return { ...r, count: p.count, sequence: e.sequence };
          case 'round.playback':
            // Live-camera round: the locked footage is scheduled; the stage preloads it and starts at startAt.
            return p.assetId ? { ...r, sequence: e.sequence, playback: { assetId: p.assetId, startAt: p.startAt as UtcInstant, revealAt: p.revealAt as UtcInstant, durationMs: Date.parse(p.revealAt) - Date.parse(p.startAt) } } : { ...r, sequence: e.sequence };
          case 'round.result':
            return { ...r, count: p.finalCount, finalCount: p.finalCount, sequence: e.sequence };
          default:
            return r;
        }
      });
      // LIVE rounds name only the country while they run; the full location comes with the finished round.
      if (e.gameRoundId && (p.type === 'round.result' || (p.type === 'round.state' && ['RESULT_FINAL', 'SETTLED', 'VOIDED'].includes(p.to)))) {
        const id = e.gameRoundId;
        void gw.game.getRound(id).then((full) => {
          if (full?.manifest.sourceMode === 'LIVE') setRound((r) => (r && r.gameRoundId === id ? { ...r, manifest: full.manifest } : r));
        });
      }
      if (p.type === 'round.crossing' && e.gameRoundId) {
        // Pulse only when the count actually increased (excluded/duplicate crossings do not flash).
        const prev = lastCount.current?.id === e.gameRoundId ? lastCount.current.count : 0;
        if (p.count > prev) setCountPulse((n) => n + 1);
        lastCount.current = { id: e.gameRoundId, count: p.count };
      }
      if (p.type === 'round.result' && e.gameRoundId) pushOutcome({ gameRoundId: e.gameRoundId, kind: 'RESULT', finalCount: p.finalCount, winning: p.winningSelections });
      if (p.type === 'round.state' && p.to === 'VOIDED' && e.gameRoundId) pushOutcome({ gameRoundId: e.gameRoundId, kind: 'VOIDED', finalCount: null, winning: [] });
    };
    const onPrivate = (e: PrivateEventEnvelope) => {
      const p = e.payload;
      if (p.type === 'bet.updated') setBets((bs) => [p.bet, ...bs.filter((b) => b.betId !== p.bet.betId)]);
      if (p.type === 'balance.changed') {
        setBalance(p.balance);
        setWalletDown(false);
      }
      if (p.type === 'session.expired') setSessionExpired(true);
    };
    const u1 = gw.game.subscribePublic(onPublic);
    const u2 = gw.game.subscribePrivate(onPrivate);
    const u3 = gw.connection.subscribe((s) => {
      setConnection(s);
      if (s === 'connected') resync();
    });
    return () => {
      u1();
      u2();
      u3();
    };
  }, [gw, resync, pushOutcome]);

  const outcome = round ? (outcomes.find((o) => o.gameRoundId === round.gameRoundId) ?? null) : null;
  useEffect(() => {
    if (!walletDown) return;
    const t = setInterval(() => {
      void gw.wallet.getBalance().then(
        (b) => {
          setBalance(b);
          setWalletDown(false);
        },
        () => undefined,
      );
    }, 15_000);
    return () => clearInterval(t);
  }, [gw, walletDown]);

  return { round, outcome, recent: outcomes, bets, balance, walletDown, connection, sessionExpired, countPulse, resync };
}

export function useNow(gw: ClientGateways, intervalMs = 250): number {
  const [now, setNow] = useState(() => gw.serverNowMs());
  useEffect(() => {
    const t = setInterval(() => setNow(gw.serverNowMs()), intervalMs);
    return () => clearInterval(t);
  }, [gw, intervalMs]);
  return now;
}

export function usePrefersReducedMotion(): boolean {
  const q = '(prefers-reduced-motion: reduce)';
  const [v, setV] = useState(() => typeof matchMedia === 'function' && matchMedia(q).matches);
  useEffect(() => {
    const m = matchMedia(q);
    const h = () => setV(m.matches);
    m.addEventListener('change', h);
    return () => m.removeEventListener('change', h);
  }, []);
  return v;
}
